import { format, formatDistanceToNow, parse } from 'date-fns';

export class DateService {

	public static ukDateFormat = 'ddMMMyyyy'; //'dd/MM/yyyy'; // For date-fns formatting
	public static ukShortDateFormat = 'ddMMMyy';
	public static ukLongDateFormat = 'd MMMM yyyy'; // For date-fns formatting
	public static ukTimeFormat = 'HH:mm'; // For date-fns formatting
	public static ukDateTimeFormat = 'dd/MM/yyyy HH:mm'; // For date-fns formatting
	public static isDateString = new RegExp(/^[0-9]{4}-[01][0-9]-[0-3][0-9]T[0-2][0-9]:[0-5][0-9]:[0-5][0-9](\.[0-9]{1,7})?[+-]?([01][0-9]:[0-5][0-9])?Z?$/);
	public static monthNameFormat = 'MMMM yyyy'; // For date-fns formatting
	public static timestamp(): number { return (new Date()).getTime(); }


	public static removeTimezoneOffset(date: Date | string): Date {
		if (date != null) {
			return new Date(date);
		}
		return null;
	}
	public static parseUkDate(value: string): Date {
		return parse(value, DateService.ukDateFormat, new Date());
	}

	public static setLocaleDateToUTC(d: Date): Date {
		return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0));
	}

	public static dateToUkDate(d: Date): string {
		const date = this.removeTimezoneOffset(d);
		return date instanceof Date ? format(date, DateService.ukDateFormat) : '';
	}

	public static dateToUkShortDate(d: Date): string {
		return d instanceof Date ? format(d, DateService.ukShortDateFormat) : '';
	}

	public static dateToUkDateTime(d: Date): string {
		const date = this.removeTimezoneOffset(d);
		return date instanceof Date ? format(date, DateService.ukDateTimeFormat) : '';
	}

	public static dateToUkLongDate(d: Date): string {
		const date = this.removeTimezoneOffset(d);
		return date instanceof Date ? format(date, DateService.ukLongDateFormat) : '';
	}

	public static dateToUkTime(d: Date): string {
		return d instanceof Date ? format(d, DateService.ukTimeFormat) : '';
	}

	public static dateToMonthNameFormat(d: Date): string {
		return d instanceof Date ? format(d, DateService.monthNameFormat) : '';
	}

	public static lastDateOfMonth = (d: Date): Date => {
		if (d instanceof Date) {
			return new Date(Date.UTC(d.getUTCFullYear(), d.getMonth() + 1, 0));
		} else {
			throw new Error('Invalid input. Expected a Date object.');
		}
	};


	public static convertODataDates(dataObject: unknown): unknown {
		DateService.convertObjectDates(dataObject);
		return dataObject;
	}

	private static convertObjectDates(dataObject: unknown): void {
		if (dataObject) {
			const obj = dataObject as { [key: string]: unknown }; // Type assertion

			Object.keys(obj).forEach(key => {
				if (typeof obj[key] === 'object') {
					DateService.convertObjectDates(obj[key]);
				} else if (DateService.isDateString.test(obj[key] as string)) {
					obj[key] = new Date(obj[key] as string);
				}
			});
		}
	}


	public static relativeToNow = (d: Date): string => {
		let date: Date = null;
		if (d != null) {
			date = new Date(d);
			console.log('instance of date', date);
		}
		else {
			console.log('not an instance of date');
		}
		return date instanceof Date ? formatDistanceToNow(date) : ``;
	}

	public static parseUkDateTime(value: string): Date {
		if (!value) return null;
		return parse(value, DateService.ukDateTimeFormat, new Date());
	}

	public static dateToReadableDateTime(d: Date | string): string {
		if (!d) return '';

		let date: Date;

		if (typeof d === 'string') {
			date = this.parseUkDateTime(d);
		} else {
			date = this.removeTimezoneOffset(d);
		}

		return date instanceof Date && !isNaN(date.getTime())
			? format(date, 'dd MMM yyyy HH:mm')
			: '';
	}





	public static toDisplayDateTime_withlogs(value: string | Date): string {
		if (!value) return '';

		console.log('RAW value:', value);
		console.log('RAW value type:', typeof value);
		console.log('Ends with Z:', typeof value === 'string' && value.endsWith('Z'));

		let normalized = value as string;
		if (typeof normalized === 'string' && !normalized.endsWith('Z') && !normalized.includes('+')) {
			normalized += 'Z';
		}

		console.log('Normalized value:', normalized);

		const date = new Date(normalized);
		console.log('Parsed date:', date);
		console.log('date.toISOString():', date.toISOString());
		console.log('date.toLocaleString():', date.toLocaleString());
		console.log('date.getHours():', date.getHours());
		console.log('date.getUTCHours():', date.getUTCHours());

		if (isNaN(date.getTime())) return '';

		const day = String(date.getDate()).padStart(2, '0');
		const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
		const month = monthNames[date.getMonth()];
		const year = date.getFullYear();
		const hours = String(date.getHours()).padStart(2, '0');
		const minutes = String(date.getMinutes()).padStart(2, '0');
		return `${day} ${month} ${year} ${hours}:${minutes}`;
	}

	public static toDisplayDateTime(value: string | Date): string {
		if (!value) return '';

		const date = new Date(value);

		if (isNaN(date.getTime())) return '';

		const day = String(date.getDate()).padStart(2, '0');
		const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
			'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
		const month = monthNames[date.getMonth()];
		const year = date.getFullYear();
		const hours = String(date.getHours()).padStart(2, '0');
		const minutes = String(date.getMinutes()).padStart(2, '0');

		return `${day} ${month} ${year} ${hours}:${minutes}`;
	}

	public static toDisplayDate(value: string | Date): string {
		if (!value) return '';

		const date = new Date(value);

		if (isNaN(date.getTime())) return '';

		const day = String(date.getDate()).padStart(2, '0');
		const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
			'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
		const month = monthNames[date.getMonth()];
		const year = date.getFullYear();

		return `${day} ${month} ${year}`;
	}

}